import SEO from "@/components/SEO";
import {
  Blockquote,
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
  SpeakerLine,
} from "./seo/SeoPageLayout";

const DarvoExamples = () => {
  return (
    <SeoPageShell>
      <SEO
        title="DARVO Examples: Deny, Attack, Reverse Victim and Offender"
        description="DARVO is a predictable response to being held accountable: deny it happened, attack the person raising it, and reverse the roles so they become the offender. Real text examples."
        path="/darvo-examples"
      />

      <HeroH1>DARVO Examples: How "I Didn't Do Anything" Becomes "You're Attacking Me"</HeroH1>

      <P>
        You raise something that hurt you. Three messages later, you are the one apologizing. The
        thing you came in to talk about has vanished. The new topic is your tone, your timing,
        your unfairness for bringing it up.
      </P>
      <P>
        That sequence has a name: DARVO. Deny, Attack, Reverse Victim and Offender. It is one of
        the most predictable responses to being asked to account for behavior, and once you can
        see it move, you stop falling into it. It is also part of{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>
      </P>

      <H2>The three moves, in order</H2>
      <P>Deny it happened. Attack the person raising it. Reverse the roles.</P>

      <Blockquote>
        <SpeakerLine speaker="You" line="It really hurt when you talked over me at dinner last night." />
        <SpeakerLine speaker="Them — DENY" line="I didn't talk over you. That's not what happened." />
        <SpeakerLine speaker="Them — ATTACK" line="Honestly, you've been looking for a reason to be upset with me all week." />
        <SpeakerLine speaker="Them — REVERSE" line="I can't believe you'd ambush me with this. Do you have any idea how exhausting it is to be on the receiving end of constant accusations?" />
      </Blockquote>

      <P>
        Three sentences. You walked in with a grievance about being talked over. You're now
        defending yourself against the charge of "constant accusations."
      </P>

      <H2>More DARVO examples in text</H2>
      <P>A missed commitment:</P>
      <Blockquote>
        <SpeakerLine speaker="You" line="You said you'd be here by seven. It's almost nine." />
        <SpeakerLine speaker="Them" line="I never said seven. You always make up timelines and then get mad I didn't meet them. This is honestly really controlling behavior." />
      </Blockquote>

      <P>A boundary being set:</P>
      <Blockquote>
        <SpeakerLine speaker="You" line="I'm not comfortable talking about my work stuff with your mom anymore." />
        <SpeakerLine speaker="Them" line="Wow. So my family isn't good enough now? You're isolating me from the people who love me. I didn't realize you saw us that way." />
      </Blockquote>

      <CTAButton />

      <H2>Why DARVO is so disorienting</H2>
      <P>
        DARVO inverts the moral roles fast enough that you lose your footing mid-conversation.
        One moment you are the person who was hurt. The next, you are the person being asked to
        justify your right to feel hurt. By the time you realize what happened, you have already
        spent twenty minutes defending yourself for something you came in to address.
      </P>
      <P>
        Recognizing the sequence is what breaks its spell. Once you can quietly note "that was
        deny… that was attack… and there's the reversal," it stops working on you the way it
        used to. It is also{" "}
        <InlineLink to="/ai-detect-narcissistic-abuse">
          how AI detection surfaces patterns like this across a full conversation →
        </InlineLink>
      </P>

      <H2>When DARVO follows a reaction</H2>
      <P>
        DARVO often lands right after a provoked reaction — the provocation is quiet, your
        reaction is loud, and then DARVO arrives to make the reaction itself the only thing
        worth discussing.{" "}
        <InlineLink to="/reactive-abuse-examples">
          See reactive abuse examples →
        </InlineLink>{" "}
        Naming sequences like this is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton />
    </SeoPageShell>
  );
};

export default DarvoExamples;
